console.log("✅ component-cart-items.js loaded");

import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */

console.log("✅ before class definition");

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
    #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity } = event.detail;

    const rowEl = event.target instanceof Element ? event.target.closest('[data-key]') : null;
    const key = rowEl?.getAttribute('data-key') || null;

    const line = this.#getLineFromEventTarget(event.target) ?? event.detail.cartLine;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, key);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      key,
    });
  }


  #reindexCartItemLines() {
    const rows = this.querySelectorAll('[ref="cartItemRows[]"]');
    rows.forEach((row, i) => row.setAttribute('data-line', String(i + 1)));
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
    onLineItemRemove(line, key = null) {
    if (!line && !key) return;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      key,
    }).catch(() => {});
  }


  /**
   * Updates the cart quantity and returns a promise that resolves
   * only when Shopify confirms the change.
   *
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   * @returns {Promise<Object>}
   */
    updateQuantity(config) {
    const cartPerformanceUpdateMarker =
      cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, key } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const doRequest = (lineToUse) => {
      const body = JSON.stringify({
        line: lineToUse,
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
      return fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    };

    cartTotal?.shimmer();

    return doRequest(line)
      .then(async (res) => {
        // ✅ If Shopify says line is invalid, resolve by key and retry once
        if (res.status === 422 && key) {
          const fixedLine = await this.#findLineByKey(key);
          if (fixedLine) return doRequest(fixedLine);
        }
        return res;
      })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          throw new Error(parsedResponseText.errors);
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount =
          newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;

        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

                this.#updateQuantitySelectors(parsedResponseText);

// ✅ Update ONLY the items list (left side) so app widgets stay alive (right side)
const newDoc = new DOMParser().parseFromString(
  parsedResponseText.sections[this.sectionId],
  'text/html'
);

// These selectors match Horizon cart structure in your screenshots
const newItems = newDoc.querySelector('.cart-page__items');
const curItems = document.querySelector('.cart-page__items');

if (newItems && curItems) {
  curItems.innerHTML = newItems.innerHTML;
}

// Update cart count (badge uses this)
const newCountEl = newDoc.querySelector('[ref="cartItemCount"]');
const curCountEl = document.querySelector(`[data-section-id="${this.sectionId}"] [ref="cartItemCount"]`);

if (newCountEl && curCountEl) {
  curCountEl.textContent = newCountEl.textContent;
}

// Re-index lines if you're using data-line
this.#reindexCartItemLines();

// Re-init widgets after DOM patch
this.#reinitExternalCartWidgets();

setTimeout(() => window.Downpay?.init?.(), 50);
setTimeout(() => window.Downpay?.init?.(), 300);


        const cartUpdateEvent = new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
          force: true,
        });

        // this.dispatchEvent(cartUpdateEvent);
        // this.#handleCartUpdate(cartUpdateEvent);


        return parsedResponseText;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
      });
  }


  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemRow =
      this.querySelector(`[data-line="${line}"]`) ||
      this.refs.cartItemRows?.find?.((row) => String(row.dataset.line) === String(line));

    const quantityInput =
      cartItemRow?.querySelector('quantity-selector-component input') ||
      cartItemRow?.querySelector('cart-quantity-selector-component input') ||
      cartItemRow?.querySelector('input[name="quantity"]') ||
      cartItemRow?.querySelector('input[name="updates[]"]') ||
      cartItemRow?.querySelector('input[type="number"]');

    if (!quantityInput) {
      console.warn('Quantity input not found for line', line);
      return;
    }

    // Revert UI back to previous value
    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #reinitExternalCartWidgets() {
    const run = () => window.__gamestReinitCartWidgets?.(this.sectionId);

    queueMicrotask(() => {
      setTimeout(run, 0);
      setTimeout(run, 250);
      setTimeout(run, 900);

      document.dispatchEvent(new Event('cart:updated'));
      window.dispatchEvent(new Event('cart:updated'));

      window.Downpay?.init?.();
    });
  }

  #getCartWidgetStash() {
    let stash = document.getElementById('gamest-cart-widget-stash');
    if (!stash) {
      stash = document.createElement('div');
      stash.id = 'gamest-cart-widget-stash';
      stash.style.display = 'none';
      document.body.appendChild(stash);
    }
    return stash;
  }

  #getSummaryContainer(rootEl) {
    return (
      rootEl.querySelector('.cart__summary-totals')?.parentElement ||
      rootEl.querySelector('.cart-page__summary') ||
      rootEl
    );
  }

  #findWidgetNodes(summaryRoot) {
    const selectors = [
      // Flare
      '#flare-date-picker-block',
      'flare-date-picker-block',
      '[id*="flare"]',
      '[class*="flare"]',

      // PickEasy
      '#zip-code-container',
      '#time-slot-container',
      '#date-selector-wrapper',
      '[id*="pickeasy"]',
      '[class*="pickeasy"]',

      // Downpay
      '[data-downpay]',
      '[id*="downpay"]',
      '[class*="downpay"]',
      '[data-block-type*="downpay"]',
    ];

    const nodes = [];
    const seen = new Set();

    selectors.forEach((sel) => {
      summaryRoot.querySelectorAll(sel).forEach((el) => {
        const top = el.closest('.shopify-app-block') || el;
        if (!summaryRoot.contains(top)) return;
        if (seen.has(top)) return;
        seen.add(top);
        nodes.push(top);
      });
    });

    return nodes;
  }

  #stashWidgetsBeforeMorph() {
    const sectionRoot = document.querySelector(`[data-section-id="${this.sectionId}"]`) || this;
    const summary = this.#getSummaryContainer(sectionRoot);
    if (!summary) return [];

    const stash = this.#getCartWidgetStash();
    const nodes = this.#findWidgetNodes(summary);

    nodes.forEach((node) => stash.appendChild(node));

    console.log('[GAMST] stashed nodes:', nodes.length);
    return nodes;
  }

  #removeFreshWidgetClonesAfterMorph(summary) {
    const killSelectors = [
      '#flare-date-picker-block',
      'flare-date-picker-block',
      '#zip-code-container',
      '#time-slot-container',
      '#date-selector-wrapper',
      '[id*="pickeasy"]',
      '[class*="pickeasy"]',
      '[data-downpay]',
      '[id*="downpay"]',
      '[class*="downpay"]',
      '[data-block-type*="downpay"]',
      '[id*="flare"]',
      '[class*="flare"]',
    ];

    const toRemove = new Set();
    killSelectors.forEach((sel) => {
      summary.querySelectorAll(sel).forEach((el) => {
        const top = el.closest('.shopify-app-block') || el;
        if (summary.contains(top)) toRemove.add(top);
      });
    });

    toRemove.forEach((n) => n.remove());
  }

  #restoreWidgetsAfterMorph(stashedNodes) {
    const tryRestore = () => {
      const sectionRoot = document.querySelector(`[data-section-id="${this.sectionId}"]`) || document;
      const cartRoot = sectionRoot.querySelector('cart-items-component') || sectionRoot;
      const summary = this.#getSummaryContainer(cartRoot);
      if (!summary) return false;

      this.#removeFreshWidgetClonesAfterMorph(summary);

      const downpayAnchor = summary.querySelector('#gamest-downpay-anchor');
      const ctas = summary.querySelector('.cart__ctas');

      stashedNodes.forEach((node) => {
        const isDownpay =
          node.matches?.('[data-block-type*="downpay"], [data-downpay], [id*="downpay"], [class*="downpay"]') ||
          node.querySelector?.('[data-block-type*="downpay"], [data-downpay], [id*="downpay"], [class*="downpay"]');

        if (isDownpay && downpayAnchor) {
          downpayAnchor.parentNode.insertBefore(node, downpayAnchor.nextSibling);
          return;
        }

        if (ctas && ctas.parentNode === summary) {
          summary.insertBefore(node, ctas);
        } else {
          summary.appendChild(node);
        }
      });

      this.#reinitExternalCartWidgets?.();
      window.Flare?.init?.();
      setTimeout(() => window.__pickeasyInit?.(), 600);
      setTimeout(() => window.__pickeasyInit?.(), 1400);
      window.Downpay?.init?.();

      console.log('[GAMST] restored nodes:', stashedNodes.length);
      return true;
    };

    let attempts = 0;
    const max = 20;

    const tick = () => {
      attempts += 1;
      if (tryRestore()) return;
      if (attempts >= max) {
        console.warn('[GAMST] restore failed after attempts:', attempts);
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    console.log('[GAMST] handleCartUpdate fired', event);

    if (
      !(event instanceof DiscountUpdateEvent) &&
      event.target === this &&
      !event.detail?.force
    ) return;

    const stashed = this.#stashWidgetsBeforeMorph();

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#restoreWidgetsAfterMorph(stashed);
      return;
    }

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#reindexCartItemLines();
    this.#restoreWidgetsAfterMorph(stashed);
    this.#updateCartQuantitySelectorButtonStates?.();
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }
  async #fetchCartJson() {
    const res = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed to fetch cart.js (${res.status})`);
    return res.json();
  }

  async #findLineByKey(key) {
    if (!key) return null;
    const cart = await this.#fetchCartJson();
    const idx = cart.items?.findIndex?.((it) => it.key === key) ?? -1;
    return idx >= 0 ? idx + 1 : null;
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }
 
  #getLineFromEventTarget(target) {
    if (!(target instanceof Element)) return null;
    const row = target.closest?.('[data-line]');
    const line = row?.getAttribute('data-line');
    const n = line ? parseInt(line, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

console.log("✅ after class definition");
console.log("✅ about to define cart-items-component");

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
  console.log("✅ defined cart-items-component");
}

